/** Outcome of parsing a user-supplied Google Docs URL (FR-11). */
export type DocUrlResult =
  | { ok: true; docId: string }
  | { ok: false; message: string };

/**
 * A Google Docs document path, with an optional per-account `/u/N/` segment:
 *   /document/d/{id}...
 *   /document/u/0/d/{id}...
 * The id is the run of URL-safe base64 characters Google uses for document ids.
 */
const DOCUMENT_PATH_RE = /^\/document\/(?:u\/\d+\/)?d\/([a-zA-Z0-9_-]+)/;

/**
 * What a document id may contain, bounded.
 *
 * The bound is not cosmetic: an id is the only thing the web host's proxy
 * accepts as input, and an unbounded id is an unbounded upstream URL. Real ones
 * are 44 characters.
 */
const DOC_ID_RE = /^[A-Za-z0-9_-]{1,256}$/;

/**
 * A document id on its own, long enough that no ordinary word reaches it.
 *
 * The floor is doing real work wherever a bare id and a piece of prose could
 * both turn up in the same field: without it, a one-word script would be read
 * as a document reference and loaded as an unshared document.
 */
const BARE_DOC_ID_RE = /^[A-Za-z0-9_-]{20,}$/;

/** Whether `value` could be a document id at all. */
export function isDocId(value: string): boolean {
  return DOC_ID_RE.test(value);
}

/** Whether `value` is a document id rather than something a person wrote. */
export function isBareDocId(value: string): boolean {
  return BARE_DOC_ID_RE.test(value);
}

/** The link a document id names — what a user would have copied out of Google
 * Docs, rebuilt from the id alone. */
export function documentUrlFor(docId: string): string {
  return `https://docs.google.com/document/d/${docId}/edit`;
}

const NOT_A_DOC_MESSAGE =
  "That's not a Google Doc. Paste a docs.google.com/document link.";

/**
 * Parse a Google Docs URL in any of its common forms and extract the document
 * id (FR-11). A URL that is not a Google Doc — a Sheets/Slides link, an
 * unrelated host, or plain text — is rejected with a specific message so the
 * caller can surface it verbatim.
 */
export function extractDocId(input: string): DocUrlResult {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return { ok: false, message: "Enter a Google Docs URL." };
  }

  const address = parseAddress(trimmed);
  if (!address) {
    return {
      ok: false,
      message:
        "That doesn't look like a URL. Paste the Google Doc's share link.",
    };
  }

  if (address.hostname !== "docs.google.com") {
    return { ok: false, message: NOT_A_DOC_MESSAGE };
  }

  // Matches a Doc path with a non-empty id, or nothing — a docs.google.com link
  // to Sheets/Slides/Forms, or /document/d/ with no id, both fail here.
  const docId = DOCUMENT_PATH_RE.exec(address.pathname)?.[1];
  if (!docId) {
    return { ok: false, message: NOT_A_DOC_MESSAGE };
  }

  return { ok: true, docId };
}

/**
 * An address, split into the two parts this needs — **without `URL`**.
 *
 * The class is not available to this package, and the reason is a host rather
 * than a preference: React Native installs its own `URL` over the runtime's
 * global, and it is a placeholder whose constructor keeps the string and whose
 * every accessor throws. Reading `hostname` off it is not a wrong answer, it is
 * an exception — thrown out of the middle of `extractDocId`, which every
 * document load on the phone goes through, and reported to the reader as
 * `"URL.hostname is not implemented"` where the sharing hint belongs. It
 * typechecks and every test against Node's real class passes, which is what made
 * it worth writing down here and refusing in `platform-agnostic.test.ts`.
 *
 * A scheme is optional, so that a pasted `docs.google.com/document/...` still
 * parses. `null` is "not a URL at all", which is a different sentence to the
 * reader from "not a Google Doc": nothing where a host goes, or whitespace
 * inside one, is somebody who pasted prose into the field.
 *
 * **Whitespace is refused in the authority and tolerated after it**, which is
 * `URL`'s own behaviour rather than a looser version of it: the class
 * percent-encodes a space in a path or a query and rejects one in a host. Being
 * stricter here would have quietly changed what ticket 14 accepts — a link
 * copied out of a wrapped email arrives with a space in the middle of it, and it
 * used to load.
 */
function parseAddress(input: string): { hostname: string; pathname: string } | null {
  const parts =
    /^(?:[a-z][a-z0-9+.-]*:\/\/)?(?<authority>[^/?#\s]+)(?<path>\/[^?#]*)?(?:[?#][\s\S]*)?$/iu.exec(
      input,
    );
  const authority = parts?.groups?.["authority"];
  if (authority === undefined) return null;
  return {
    // The host alone: whatever is in front of an `@` is a user and whatever is
    // behind a `:` is a port, and `URL.hostname` is neither of them. Lower-cased
    // because a host is, and the comparison above is an exact one — "ends with
    // docs.google.com" would hand `docs.google.com.example.test` every document
    // anybody pasted.
    hostname: authority
      .slice(authority.lastIndexOf("@") + 1)
      .replace(/:\d*$/u, "")
      .toLowerCase(),
    // An address with no path at all has `/`, which is what `URL` answers and
    // what the pattern above then declines to find an id in.
    pathname: parts?.groups?.["path"] ?? "/",
  };
}
